import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EpcMobilizationCockpit } from '@/components/epc/commercial-ux/EpcMobilizationCockpit';
import {
  buildMobilizationReadiness,
  readinessTagColor,
  type MobilizationReadinessState,
} from '@/components/epc/commercial-ux/mobilizationReadiness';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { formatDateTime } from '@/utils/format';

const { Title, Text } = Typography;

const EpcMobilizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [focusId, setFocusId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();

  const listQ = useQuery({
    queryKey: ['epc-mobilizations'],
    queryFn: () => epcCommercialService.listMobilizations(),
    staleTime: 15_000,
  });

  const contractsQ = useQuery({
    queryKey: ['epc-contracts'],
    queryFn: () => epcCommercialService.listContracts(),
    staleTime: 30_000,
    enabled: createOpen,
  });

  const rows = (listQ.data || []) as Record<string, unknown>[];

  /** Light readiness: fetch details for visible rows (capped) for checklist counts. */
  const detailQueries = useQueries({
    queries: rows.slice(0, 30).map((r) => ({
      queryKey: ['epc-mobilization', String(r.id)],
      queryFn: () => epcCommercialService.getMobilization(String(r.id)),
      staleTime: 30_000,
      enabled: rows.length > 0,
    })),
  });

  const readinessById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof buildMobilizationReadiness>>();
    detailQueries.forEach((q, idx) => {
      const id = String(rows[idx]?.id || '');
      if (id && q.data) {
        map.set(
          id,
          buildMobilizationReadiness({
            mobilization: q.data,
            items: (q.data.items as Record<string, unknown>[]) || [],
          })
        );
      }
    });
    return map;
  }, [detailQueries, rows]);

  const createM = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcCommercialService.createMobilization(values),
    onSuccess: (created) => {
      message.success('Mobilization created');
      setCreateOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-mobilizations'] });
      if (created?.id) setFocusId(String(created.id));
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  useEffect(() => {
    const focus = searchParams.get('focus');
    if (focus) setFocusId(focus);
  }, [searchParams]);

  const activeContracts = ((contractsQ.data || []) as Record<string, unknown>[]).filter(
    (c) => String(c.status || '') === 'Active'
  );

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Mobilizations
          </Title>
          <Text type="secondary">
            Checklist readiness → Approval Runtime → activate project (from Active EPC Contract).
          </Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          New
        </Button>
      </Space>

      <Card>
        <Table
          loading={listQ.isLoading}
          rowKey={(r) => String(r.id)}
          dataSource={rows}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1000 }}
          onRow={(r) => ({
            onClick: () => setFocusId(String(r.id)),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: 'Number', dataIndex: 'mobilization_number', width: 140 },
            {
              title: 'Readiness',
              key: 'readiness',
              width: 130,
              render: (_: unknown, r) => {
                const view = readinessById.get(String(r.id));
                if (!view) return <Tag>…</Tag>;
                return (
                  <Tag color={readinessTagColor(view.overall as MobilizationReadinessState)}>
                    {view.overall}
                  </Tag>
                );
              },
            },
            {
              title: 'Checklist',
              key: 'checklist',
              width: 110,
              render: (_: unknown, r) => {
                const view = readinessById.get(String(r.id));
                if (!view) return '—';
                return `${view.requiredComplete}/${view.requiredTotal}`;
              },
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 120,
              render: (s: string) => <Tag>{s}</Tag>,
            },
            {
              title: 'Contract',
              dataIndex: 'contract_id',
              width: 110,
              render: (v) =>
                v ? (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/epc/commercial/contracts/${v}`);
                    }}
                  >
                    {String(v).slice(0, 8)}
                  </Button>
                ) : (
                  '—'
                ),
            },
            {
              title: 'Project',
              dataIndex: 'project_id',
              width: 110,
              render: (v) =>
                v ? (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/prj/projects/${v}/edit`);
                    }}
                  >
                    {String(v).slice(0, 8)}
                  </Button>
                ) : (
                  '—'
                ),
            },
            {
              title: 'Activated',
              dataIndex: 'activated_at',
              width: 160,
              render: (v) => (v ? formatDateTime(String(v)) : '—'),
            },
          ]}
        />
      </Card>

      <Drawer
        title="Mobilization cockpit"
        open={Boolean(focusId)}
        onClose={() => {
          setFocusId(null);
          if (searchParams.has('focus')) {
            const next = new URLSearchParams(searchParams);
            next.delete('focus');
            setSearchParams(next, { replace: true });
          }
        }}
        width={Math.min(560, typeof window !== 'undefined' ? window.innerWidth - 24 : 560)}
        destroyOnClose
      >
        {focusId ? (
          <EpcMobilizationCockpit
            mobilizationId={focusId}
            onUpdated={() => listQ.refetch()}
          />
        ) : null}
      </Drawer>

      <Modal
        title="New mobilization"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={createM.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            createM.mutate({
              contractId: values.contractId,
              title: values.title || undefined,
              departmentId: values.department_id,
            });
          }}
        >
          <Form.Item
            name="contractId"
            label="Active EPC Contract"
            rules={[{ required: true, message: 'Required' }]}
            extra="Contract must be Active (domain rule)."
          >
            <Select
              showSearch
              optionFilterProp="label"
              loading={contractsQ.isLoading}
              options={activeContracts.map((c) => ({
                value: String(c.id),
                label: `${c.contract_number || String(c.id).slice(0, 8)} — ${c.title || 'Contract'}`,
              }))}
              placeholder={
                activeContracts.length ? 'Select contract' : 'No Active contracts found'
              }
            />
          </Form.Item>
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
            extra="Defaults from the EPC contract when set; confirm or override."
          >
            <DepartmentSelect />
          </Form.Item>
          <Form.Item name="title" label="Title">
            <Input placeholder="Optional title" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EpcMobilizationsPage;
